import { Banner, type BannerProps } from '@imprivata-cloud/components';
import { Fragment, type PropsWithChildren, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { errorConfiguration } from '../ErrorConfig';
import { useErrorContext } from './ErrorContext';

export type NotificationProps = { translationRoot?: string };

export const Notifications = ({
  translationRoot,
  children,
}: PropsWithChildren<NotificationProps>) => {
  const { t } = useTranslation();
  const { error, clearErrorNotifications } = useErrorContext();

  const banner = useMemo(() => {
    if (!error) {
      return undefined;
    }
    const errorCode = error.code || error.details?.code;
    const errorConfig =
      errorConfiguration && errorCode in errorConfiguration
        ? errorConfiguration[errorCode]
        : undefined;
    let bannerMsg: BannerProps;
    if (errorConfig) {
      // Create i18n key for error message lookup
      const errorKey = translationRoot
        ? `${translationRoot}.errors.${errorConfig.errorCode}`
        : `errors.${errorConfig.errorCode}`;
      bannerMsg = {
        key: errorKey,
        type: errorConfig.type,
        message: t(errorKey as never, { defaultValue: t('errors.something-unexpected') }),
      };
    } else {
      bannerMsg = {
        key: 'unknown-error',
        type: 'error',
        message: t('errors.something-unexpected'),
      };
    }
    return bannerMsg;
  }, [t, translationRoot, error]);

  return (
    <>
      {banner && (
        <Banner
          key={banner.key}
          onClose={clearErrorNotifications}
          message={banner.message}
          type={banner.type}
          duration={10}
        />
      )}
      <Fragment key={'initial-notif-render'}>{children}</Fragment>
    </>
  );
};
